import React, { useState, useMemo } from 'react';
import { Plus, Copy, Check, Key, School as SchoolIcon, Trash2, RefreshCw, Shield, MapPin, Download } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { Modal } from '../../components/ui/Modal';
import { pdfService } from '../../services';
import { useSchools, useSchoolMutations, useLogMutations } from '../../hooks/useConvex';
import { useAuth } from '../../contexts/AuthContext';
import { School } from '../../types';
import { Id } from '../../../convex/_generated/dataModel';

export const AccessKeysPage: React.FC = () => {
    const { user } = useAuth();
    const schoolsData = useSchools();
    const { create, remove } = useSchoolMutations();
    const { logAdminActivity } = useLogMutations();
    const loading = schoolsData === undefined;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [schoolName, setSchoolName] = useState('');
    const [branchName, setBranchName] = useState('');
    const [generatedKey, setGeneratedKey] = useState<{ school_id: string; access_key: string } | null>(null);
    const [copiedKey, setCopiedKey] = useState('');
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Transform Convex data to School type and sort
    const schools = useMemo(() => {
        if (!schoolsData) return [];
        const transformed = schoolsData.map((s: Record<string, unknown>) => ({
            ...s,
            school_id: s._id,
            created_at: s._creationTime ? new Date(s._creationTime as number).toISOString() : undefined,
        })) as School[];

        // Sort by creation date if available, otherwise by name
        return transformed.sort((a: School, b: School) => {
            if (a.created_at && b.created_at) {
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            }
            return a.school_name.localeCompare(b.school_name);
        });
    }, [schoolsData]);

    const handleGenerateKey = async () => {
        if (!schoolName.trim() || !branchName.trim()) {
            alert('Please enter both school name and branch name');
            return;
        }

        try {
            setActionLoading(true);
            // Generate a unique access key
            const accessKey = Math.random().toString(36).substring(2, 8).toUpperCase();

            const result = await create({
                schoolName: schoolName.trim(),
                branchName: branchName.trim(),
                createdBy: user?.email || 'admin',
            });

            setGeneratedKey({ school_id: result.schoolId as string, access_key: result.accessKey });

            // Log activity
            await logAdminActivity({
                email: user?.email || '',
                action: 'create_school_key',
                details: `Created school key for ${schoolName.trim()} - ${branchName.trim()}`,
            });

            setSchoolName('');
            setBranchName('');
            setIsModalOpen(false);
        } catch (error: any) {
            alert(error.message || 'Failed to generate key');
        } finally {
            setActionLoading(false);
        }
    };

    const copyToClipboard = (key: string) => {
        navigator.clipboard.writeText(key);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(''), 2000);
    };

    const handleDeleteSchool = async (schoolId: string, schoolName: string) => {
        if (!window.confirm(`Are you sure you want to delete "${schoolName}"? This action cannot be undone.`)) {
            return;
        }

        setDeletingId(schoolId);
        try {
            await remove({ schoolId: schoolId as Id<'schools'> });

            // Log activity
            await logAdminActivity({
                email: user?.email || '',
                action: 'delete_school',
                details: `Deleted school: ${schoolName}`,
            });
        } catch (error: any) {
            console.error('Error deleting school:', error);
            alert(error.message || 'Failed to delete school');
        } finally {
            setDeletingId(null);
        }
    };

    const handleExportPDF = () => {
        const data = schools.map(school => [
            school.school_name,
            school.branch_name || 'Main Branch',
            school.access_key || 'N/A',
            school.created_at ? new Date(school.created_at).toLocaleDateString() : 'N/A',
            school.is_active ? 'Active' : 'Inactive'
        ]);

        pdfService.generatePDF({
            title: 'School Access Keys',
            filename: 'access_keys',
            columns: ['School Name', 'Branch', 'Access Key', 'Created At', 'Status'],
            data: data,
            orientation: 'landscape',
        });
    };

    return (
        <div className="space-y-8">
            {/* Page Header */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand/[0.08] border border-brand/[0.12] flex items-center justify-center">
                            <Key className="w-5 h-5 text-brand" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-display font-bold text-white tracking-tight">Access Keys</h1>
                            <p className="text-sm text-white/30 mt-0.5">{schools.length} schools • Secure authentication</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.reload()}
                            isLoading={loading}
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleExportPDF}>
                            <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="primary" onClick={() => setIsModalOpen(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Generate Key
                        </Button>
                    </div>
                </div>
            </div>

            {/* Generated Key Banner */}
            {generatedKey && (
                <Card gradientBorder className="p-5 border-brand/20">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                        <div className="flex items-center gap-3">
                            <Key className="w-5 h-5 text-brand" />
                            <div>
                                <h3 className="text-base font-semibold text-white">Access Key Generated</h3>
                                <p className="text-xs text-white/30 mt-0.5">Copy this key for school registration</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 flex-1 md:justify-end">
                            <code className="font-mono text-lg text-brand bg-brand/[0.05] px-4 py-2 rounded-lg border border-brand/[0.1]">
                                {generatedKey.access_key}
                            </code>
                            <Button
                                onClick={() => copyToClipboard(generatedKey.access_key)}
                                variant="secondary"
                                size="sm"
                            >
                                {copiedKey === generatedKey.access_key ? (
                                    <>
                                        <Check className="w-4 h-4 mr-2" />
                                        Copied
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4 mr-2" />
                                        Copy
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            {/* Keys Table */}
            <Card className="overflow-hidden">
                <div className="px-6 py-4 border-b border-white/[0.06] flex justify-between items-center">
                    <h2 className="text-sm font-display font-semibold text-white">School Access Keys</h2>
                    <Badge variant="neutral" size="sm">{schools.length} Total</Badge>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-white/[0.02]">
                            <tr>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">School</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Branch</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Access Key</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Created</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Status</th>
                                <th className="px-6 py-3.5 text-right text-[11px] font-medium uppercase tracking-wider text-white/25">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.03]">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <Loader size="md" className="mx-auto" />
                                    </td>
                                </tr>
                            ) : schools.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-12 h-12 rounded-full bg-white/[0.03] flex items-center justify-center">
                                                <Key className="w-6 h-6 text-white/20" />
                                            </div>
                                            <p className="text-lg font-medium text-white/40">No access keys yet</p>
                                            <p className="text-sm text-white/20">Generate a new key to get started</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                schools.map((school) => (
                                    <tr key={school.school_id} className="hover:bg-white/[0.02] transition-colors duration-150">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <SchoolIcon className="w-4 h-4 text-brand" />
                                                <span className="text-white font-medium">
                                                    {school.school_name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 text-white/50">
                                                <MapPin className="w-3 h-3" />
                                                <span className="text-sm">{school.branch_name || 'Main Branch'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <code className="font-mono text-sm text-brand bg-brand/[0.04] px-2 py-1 rounded">
                                                {school.access_key}
                                            </code>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-white/25 font-mono">
                                            {school.created_at ? new Date(school.created_at).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant={school.is_active ? 'success' : 'error'} size="sm">
                                                {school.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => copyToClipboard(school.access_key || '')}
                                                    className="text-white/30 hover:text-white hover:bg-white/[0.05]"
                                                    title="Copy Key"
                                                >
                                                    {copiedKey === school.access_key ? (
                                                        <Check className="w-4 h-4 text-success" />
                                                    ) : (
                                                        <Copy className="w-4 h-4" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDeleteSchool(school.school_id, school.school_name)}
                                                    className="text-white/30 hover:text-danger hover:bg-danger/10"
                                                    title="Delete School"
                                                    isLoading={deletingId === school.school_id}
                                                    disabled={!!deletingId}
                                                >
                                                    {deletingId !== school.school_id && <Trash2 className="w-4 h-4" />}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Generate Key Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Generate Access Key"
                size="md"
            >
                <div className="space-y-6">
                    <div className="space-y-4">
                        <Input
                            label="School Name"
                            value={schoolName}
                            onChange={(e) => setSchoolName(e.target.value)}
                            placeholder="e.g., Seth M.R. Jaipuria School"
                            fullWidth
                        />
                        <Input
                            label="Branch Name"
                            value={branchName}
                            onChange={(e) => setBranchName(e.target.value)}
                            placeholder="e.g., Gomti Nagar"
                            fullWidth
                        />
                    </div>

                    <Card className="p-4 bg-brand/[0.03] border-brand/20">
                        <div className="flex gap-4">
                            <div className="w-10 h-10 rounded-lg bg-brand/[0.1] border border-brand/20 flex items-center justify-center flex-shrink-0">
                                <Shield className="w-5 h-5 text-brand" />
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-white mb-1">Secure Generation</h4>
                                <p className="text-xs text-white/40 leading-relaxed">
                                    A unique, cryptographically secure access key will be generated for this school.
                                    This key grants access to the School Panel for registration and team management.
                                </p>
                            </div>
                        </div>
                    </Card>

                    <div className="flex justify-end gap-3 pt-2">
                        <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleGenerateKey}
                            isLoading={actionLoading}
                            disabled={!schoolName || !branchName}
                        >
                            <Key className="w-4 h-4 mr-2" />
                            Generate Key
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
